import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { twJoin } from 'tailwind-merge';

/** The widths a screen's body column is capped at, written out whole so Tailwind generates them. */
export type ScreenColumnWidth =
  | 'max-w-screen-md'
  | 'max-w-screen-xl'
  | 'max-w-[850px]'
  | 'max-w-[1080px]';

/** The side gutter of a screen's body: 20pt on a phone, 40pt from `md` up. */
export const SCREEN_GUTTER_CLASS = 'px-5 md:px-10';

type ScreenColumnOptions = {
  maxWidth?: ScreenColumnWidth;
  gutter?: string;
};

type ScreenColumnClasses = {
  /** Goes on the view around the scroll view, or on the scroll view's own `className`. */
  frame: string;
  /** Goes on the scroll view's `contentContainerClassName`. */
  content: string;
};

/**
 * Where a screen's centered column and side gutter go when its body scrolls under the header.
 *
 * On native the header floats, and iOS draws the bar's soft edge across the scroll view's own
 * frame. A gutter or a capped width on the scroll view, or on any view around it, narrows that
 * frame, so the fade stops short of the screen's sides and the content looks cut off under the
 * bar. Native therefore pads the scroll view's content with the gutter and leaves the frame
 * as wide as the screen. The width cap stays on the frame: every cap is wider than a phone, so
 * it only narrows the frame on a tablet, and a width on a keyboard-aware scroll view's content
 * sizes it wider than the screen.
 *
 * Web has no floating bar, and its header renders inside the same column as the body, so the
 * frame keeps the column there and the page lays out exactly as before.
 */
export function getScreenColumnClasses({
  maxWidth,
  gutter = SCREEN_GUTTER_CLASS,
}: ScreenColumnOptions = {}): ScreenColumnClasses {
  if (isWeb) {
    return { frame: twJoin('mx-auto w-full', maxWidth, gutter), content: '' };
  }

  return { frame: twJoin('mx-auto w-full', maxWidth), content: gutter };
}
